import React from "react";
import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Table from "@mui/material/Table";
import Typography from "@mui/material/Typography";
import TableCell from "@mui/material/TableCell";
import TableRow from "@mui/material/TableRow";
import TableBody from "@mui/material/TableBody";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import {
    elementIncludesImage,
    enrichWithUuid,
    getAlignClass,
    getAttribute,
    getCtaButtonClass,
    getElementTextAlign,
    isUrlAbsolute,
    ListType,
    RichTextElement,
    RichTextProps
} from "./CCORichTextHelper";
import C041Video from "../../content/C041Video/C041Video";
import PlacementItem from "../../layouts/body/PlacementItem";
import {
    generateDownloadUrl,
    getIdFromDataHref,
    getIdFromHref,
    resolveLinkWithPlaceholder
} from "../../../utility/navigationUtil";
import C067HtmlFragment from "../../content/C067HtmlFragment/C067HtmlFragment";
import {
    blockQuoteStyle,
    imgInnerBoxStyle,
    imgOuterBoxStyle,
    MARGINS,
    olLiStyle,
    olStyle,
    ulLiStyle,
    ulStyle
} from "./CCORichTextStyles";
import CCODownloadButton from "../CCODownloadButton";
import CCOOpenInNewTabIcon from "../CCOOpenInNewTabIcon";
import {TypographyVariant} from "@mui/material/styles";
import CCOHeadline from "../CCOHeadline";
import {t} from "i18next";
import Button from "@mui/material/Button";
import {BUTTON_DEFAULT_COLOR, buttonClasses} from "../../layouts/navigation/QuickAccessButton";
import {CmHTMLType, CmVideoType} from "../../types";
import type {CAEGlobalSettings} from "../../../types/CAEGlobalSettings";
import {createLinkClickHandler} from "tracking/handlers/linkClickHandler";

const getTarget = (element: RichTextElement) => element?.attributes?.find(
    (attribute) => attribute.name === 'data-show')?.value === 'new' ? '_blank' : '_self';
const isDownloadTarget = (link: string) => link?.includes(":CMDownload");
const getDownloadTargetLink = (link: string) => {
    const page = link?.split('#')?.[1];
    return generateDownloadUrl(getIdFromHref(link)) + (page ? '#page=' + page : '');
}

export function renderShowMoreContainerElement(element: RichTextElement,
                                               renderChildrenRecursively: (...args: any[]) => JSX.Element | null,
                                               showMore: boolean | undefined,
                                               setShowMore: (...args: any[]) => void,
                                               key: any): JSX.Element {
    return (
        <React.Fragment key={element.uuid}>
            {(element.children && element.children[0] && element.children[0].children) ?
                <React.Fragment
                    key={element.uuid}>{(element.children![0].children ?? []).map((child, index) => (
                    <React.Fragment key={child?.uuid ?? `${element.uuid ?? 'rt'}-0-${index}`}>
                        {renderChildrenRecursively(child, index)}
                    </React.Fragment>
                ))}</React.Fragment>
                : null}
            {(element.children && element.children[1] && element.children[1].children) ?
                <Box key={element.uuid} sx={{
                    marginTop: 8
                }}>
                    <Collapse key={element.uuid} in={showMore} timeout="auto" unmountOnExit
                              sx={{marginBottom: 8}}>
                        {(element.children![1].children ?? []).map((child, index) => (
                            <React.Fragment key={child?.uuid ?? `${element.uuid ?? 'rt'}-1-${index}`}>
                                {renderChildrenRecursively(child, index)}
                            </React.Fragment>
                        ))}
                    </Collapse>
                    <Divider key={key + "-divider"}></Divider>
                    <Box
                        key={key + "-box"}
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            marginTop: 6
                        }}>
                        <Typography
                            key={key + "-text"}
                            variant={'linkLabel'}
                            onClick={() => setShowMore(!showMore)}
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                color: 'primary.main',
                                marginX: 1
                            }}>{showMore ?
                            t("coo_show_less") : t("coo_show_more")}
                            {showMore ? <ExpandLess key={key + "-less"}/> :
                                <ExpandMore key={key + "-more"}/>}
                        </Typography>
                    </Box>
                </Box> : null}
        </React.Fragment>
    );
}

export function renderMediaElement(element: RichTextElement,
                                   key: any,
                                   props?: RichTextProps & {
                                       caeGlobalSettings?: CAEGlobalSettings
                                   }): JSX.Element {
    const srcDataAttribute = getAttribute(element.attributes!, "data-src")

    const isVideo = srcDataAttribute.includes(":CMVideo")
    const isPicture = srcDataAttribute.includes(":CMPicture")

    let videoUri = ""
    if (isVideo) {
        videoUri = srcDataAttribute.split(":CMVideo")[0]
    }
    const className = getAttribute(element.attributes!, "class")

    if (!className) {
        return isVideo ?
            <C041Video key={element.uuid} video={{dataUrl: videoUri} as CmVideoType}
                       noContainer={true}></C041Video>
            : isPicture ?
                <Box
                    key={element.uuid}
                    sx={[{
                        marginTop: 4,
                        width: '100%'
                    }, imgOuterBoxStyle()]}>
                    <Box
                        key={element.uuid + "-box"}
                        sx={[{
                            width: '100%'
                        }, imgInnerBoxStyle()]}>
                        <PlacementItem key={element.uuid} item={{type: 'CMPicture'}}
                                       CAEGlobalSettings={(props?.caeGlobalSettings ||
                                           ({} as CAEGlobalSettings))}
                                       csrfToken={''}
                                       richTextItemId={getIdFromHref(srcDataAttribute)}/>
                    </Box>
                </Box>
                // If the element is not an image of video it is a HTMLFragment (work around because MarkUp renders HTML fragment under <img> tag)
                :
                <C067HtmlFragment key={element.uuid} cmHtml={{html: srcDataAttribute} as CmHTMLType}
                                  isPreview={true}></C067HtmlFragment>;
    }

    const float: 'left' | 'right' | undefined = className.split('--')[1] as 'left' | 'right' ??
        undefined
    return <Box key={key} sx={imgOuterBoxStyle(float)}>
        <Box key={key + "-box"} sx={imgInnerBoxStyle(float)}>
            {isVideo ?
                <C041Video key={key + "-video"} video={{dataUrl: videoUri} as CmVideoType}
                           noContainer={true}></C041Video>
                :
                <PlacementItem key={element.uuid} item={{type: 'CMPicture', clazz: float}}
                               CAEGlobalSettings={(props?.caeGlobalSettings ||
                                   ({} as CAEGlobalSettings))}
                               csrfToken={''}
                               richTextItemId={getIdFromHref(srcDataAttribute)}/>
            }
        </Box>
    </Box>
}

export function renderLinkElement(element: RichTextElement,
                                  props: RichTextProps & { caeGlobalSettings?: CAEGlobalSettings },
                                  renderChildrenRecursively: (...args: any[]) => JSX.Element | null,
                                  key: any, parentComponentId: string,
                                  pageId: string): JSX.Element {
    if (props.noLinks) {
        return <div key={element.uuid}>{element.children?.map((child, index) => (
            <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-link-${index}`}>
                {renderChildrenRecursively(child, index)}
            </React.Fragment>
        ))}</div>
    }
    const href = getAttribute(element.attributes!, "data-href") ??
        getAttribute(element.attributes!, "href")
    if (href.includes(":CMVideo")) {
        return <C041Video key={element.uuid}
                          video={{dataUrl: href.split(':CMVideo')[0]} as CmVideoType}
                          noContainer={true}/>
    }
    const isCMCollection = href.includes(":CMCollection")
    if (isCMCollection) {
        const collectionId = getIdFromHref(href)
        return <PlacementItem key={key} item={{type: 'CMCollection'}}
                              CAEGlobalSettings={(props.caeGlobalSettings ||
                                  ({} as CAEGlobalSettings))}
                              csrfToken={''}
                              richTextItemId={collectionId}/>

    }

    const isCMPlaceholder = href.includes(":CMPlaceholder")
    if (isCMPlaceholder) {
        const placeholderId = getIdFromHref(href)
        return <PlacementItem key={key} item={{type: 'CMPlaceholder'}}
                              CAEGlobalSettings={(props.caeGlobalSettings ||
                                  ({} as CAEGlobalSettings))}
                              csrfToken={''}
                              richTextItemId={placeholderId}/>
    }

    if (isDownloadTarget(href)) {
        const hrefLink = getDownloadTargetLink(href);
        return renderDownloadLinkElement(element, hrefLink, key, 'internalDownloadFromRichText',
            renderChildrenRecursively)
    }

    const isDownload = href.includes("/caas/v1/media")
    if (isDownload && !props.renderDownloadAsLink) {
        const downloadTitle = element?.children && element.children.length > 0 ?
            element!.children![0].data : undefined
        return <CCODownloadButton key={element.uuid} download={[{id: getIdFromDataHref(href)}]}
                                  hideSize={true}
                                  showDocumentTitle={true}
                                  downloadTitle={downloadTitle}
                                  target={getTarget(element)}/>
    }

    // localStorage/sessionStorage don't exist during SSR; resolveLinkWithPlaceholder only reads
    // from them when the href actually contains a `{{placeholder}}` template, falling back to that
    // placeholder's default value otherwise - passing no storages server-side is safe and lets that
    // fallback apply, rather than crashing on the (eagerly evaluated) array literal below.
    const storages = typeof window !== 'undefined' ? [localStorage, sessionStorage] : null;
    const hrefLink = isDownload ? generateDownloadUrl(getIdFromDataHref(href)) :
        resolveLinkWithPlaceholder(href, storages, parentComponentId, pageId);
    if (hrefLink === null) {
        return <></>;
    }
    return renderDownloadLinkElement(element, hrefLink, key, 'linkInRichText',
        renderChildrenRecursively);
}

function renderDownloadLinkElement(element: RichTextElement,
                                   href: string, key: any,
                                   dataCypress: string,
                                   renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    const target = getTarget(element);
    const openInNewWindow = target === '_blank';
    const isExternal = isUrlAbsolute(href);
    return <Link data-cypress={dataCypress}
                 data-info={dataCypress}
                 id={'txt-' + key}
                 key={element.uuid}
                 href={href}
                 underline='hover'
                 variant='linkLabel'
                 target={target}
                 onClick={createLinkClickHandler({
                     href,
                     linkTarget: target as any,
                     isExternalOverride: isExternal,
                 })}
                 style={{fontSize: 'inherit'}}>
        {(isExternal || openInNewWindow) && <CCOOpenInNewTabIcon/>}
        {element.children?.map((child, index) => (
            <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-a-${index}`}>
                {renderChildrenRecursively(child, index)}
            </React.Fragment>
        ))}
    </Link>
}

export function renderLiElement(listType: ListType | undefined, element: RichTextElement,
                                renderChildrenRecursively: (...args: any[]) => JSX.Element | null): JSX.Element {
    return listType === 'ul' ?
        <Box
            key={element.uuid}
            component={"li"}
            sx={[{
                textAlign: 'start'
            }, ...(Array.isArray(ulLiStyle) ? ulLiStyle : [ulLiStyle])]}>
            {element.children?.map((child, index) => (
                <React.Fragment
                    key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-li-ul-${index}`}>
                    {renderChildrenRecursively(child, index)}
                </React.Fragment>
            ))}
        </Box>
        : listType === 'ol' ?
            <Typography key={element.uuid} variant="body1" component="li" sx={olLiStyle}>
                {element.children?.map((child, index) => (
                    <React.Fragment
                        key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-li-ol-${index}`}>
                        {renderChildrenRecursively(child, index)}
                    </React.Fragment>
                ))}
            </Typography>
            : <Box key={element.uuid} component={"li"}
                   sx={{
                       textAlign: 'start'
                   }}>{element.children?.map((child, index) => (
                <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-li-${index}`}>
                    {renderChildrenRecursively(child, index)}
                </React.Fragment>
            ))}</Box>;
}

export function renderTextElement(element: RichTextElement,
                                  props: RichTextProps,
                                  renderChildrenRecursively: (...args: any[]) => JSX.Element | null,
                                  key: any,
                                  color: string | undefined,
                                  strong: boolean | undefined): JSX.Element | null {
    const isParagraph = element.name === 'p';
    if (isParagraph) {
        const className = getAttribute(element.attributes!, "class")
        const ctaButtonClass = className && getCtaButtonClass(className)
        if (ctaButtonClass) {
            return renderCtaButtonElement(element, ctaButtonClass, getCtaButtonJustification(className))
        }
    }
    const isParagraphWithImage = isParagraph && elementIncludesImage(element);
    const isSingleHeadlineElement = props.headlineElement && props.headlineElement ===
        element.name && element.children?.length === 1

    const marginTop = key === '0' ? 0 : MARGINS[element.name!]?.top;
    const marginBottom = MARGINS[element.name!]?.bottom;
    const variant: any = props.variant ?? (isParagraph ? "body1" :
        (element.name !== "div" ? (element.name as TypographyVariant) : undefined));

    if (isParagraphWithImage) {
        return <React.Fragment key={element.uuid}>
            {element.children?.map((child, index) => (
                <React.Fragment
                    key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-pimg-${index}`}>
                    {renderChildrenRecursively(child, index)}
                </React.Fragment>
            ))}
        </React.Fragment>
    }

    if (isSingleHeadlineElement) {
        return <CCOHeadline headline={element.children?.[0].data} key={element.uuid} margin={{b: 0}}
                            type={"componenthead"}/>
    } else {
        if (!element.uuid) {
            enrichWithUuid(element);
        }
        return (
            <React.Fragment key={element.uuid}>
                <Typography
                    align={getElementTextAlign(props, element)}
                    classes={{
                        root: element.attributes?.map(
                            attribute => attribute.name === "class" ? attribute.value : '').join(
                            ' ')
                    }}
                    component={isParagraph ? 'div' : element.name! as React.ElementType}
                    gutterBottom
                    key={element.uuid}
                    id={props.id}
                    variant={variant}
                    sx={{
                        color: color ?? undefined,
                        fontWeight: strong ? 500 : undefined,
                        marginBottom: marginBottom,
                        marginTop: marginTop
                    }}>
                    {element.children?.map((child, index) => (
                        <React.Fragment
                            key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-txt-${index}`}>
                            {renderChildrenRecursively(child, index)}
                        </React.Fragment>
                    ))}
                </Typography>
            </React.Fragment>
        );
    }
}

export function renderBlockquoteElement(element: RichTextElement,
                                        props: RichTextProps,
                                        renderElement: (...args: any[]) => JSX.Element | null,
                                        showMore: boolean | undefined,
                                        setShowMore: (...args: any[]) => void,
                                        parentComponentId: string,
                                        pageId: string) {
    return (
        <blockquote key={element.uuid}><Box key={element.uuid}
                                                   sx={{display: 'flex', justifyContent: 'center'}}>
            <Typography
                key={element.uuid}
                variant={'blockQuote'}
                sx={[{
                    color: 'primary.main'
                }, ...(Array.isArray(blockQuoteStyle) ? blockQuoteStyle : [blockQuoteStyle])]}>
                {element.children?.map((e: RichTextElement, index: number) =>
                    renderElement(props, e, parentComponentId, pageId,
                        index.toString(), 'blockQuote', showMore,
                        setShowMore, t))}
            </Typography>
        </Box></blockquote>
    );
}

export function renderStrongElement(element: RichTextElement,
                                    renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    return <Typography key={element.uuid} style={{
        fontFamily: 'var(--font-family-bold)',
        fontWeight: 'bold',
        fontSize: 'inherit'
    }} component="strong">
        {element.children?.map((child, index) => (
            <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-strong-${index}`}>
                {renderChildrenRecursively(child, index)}
            </React.Fragment>
        ))}
    </Typography>
}

export function renderEmElement(element: RichTextElement,
                                renderChildrenRecursively: (...args: any[]) => JSX.Element | null,
                                color: string | undefined) {
    return <Typography key={element.uuid} component="em" sx={{color: color ?? undefined}}
                       style={{fontSize: 'inherit'}}>
        {element.children?.map((child, index) => (
            <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-em-${index}`}>
                {renderChildrenRecursively(child, index)}
            </React.Fragment>
        ))}
    </Typography>
}

export function renderUlElement(element: RichTextElement,
                                props: RichTextProps,
                                renderElement: (...args: any[]) => JSX.Element | null,
                                showMore: boolean | undefined,
                                setShowMore: (...args: any[]) => void,
                                parentComponentId: string,
                                pageId: string) {
    return <ul key={element.uuid} style={ulStyle}>
        {element.children?.map((e, index) => (
            <React.Fragment key={(e as any)?.uuid ?? `${element.uuid ?? 'rt'}-ul-${index}`}>
                {renderElement(props, e, parentComponentId, pageId,
                    index.toString(), props.variant, showMore,
                    setShowMore, 'ul')}
            </React.Fragment>
        ))}
    </ul>
}

export function renderOlElement(element: RichTextElement,
                                props: RichTextProps,
                                renderElement: (...args: any[]) => JSX.Element | null,
                                showMore: boolean | undefined,
                                setShowMore: (...args: any[]) => void,
                                parentComponentId: string,
                                pageId: string) {
    return <ol key={element.uuid} style={olStyle}>
        {element.children?.map((e, index) => (
            <React.Fragment key={(e as any)?.uuid ?? `${element.uuid ?? 'rt'}-ol-${index}`}>
                {renderElement(props, e, parentComponentId, pageId,
                    index.toString(), props.variant, showMore,
                    setShowMore, 'ol')}
            </React.Fragment>
        ))}
    </ol>
}

export function renderDefaultElement(element: RichTextElement,
                                     renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    return <React.Fragment key={element.uuid}>
        {element.children?.map((child, index) => (
            <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-def-${index}`}>
                {renderChildrenRecursively(child, index)}
            </React.Fragment>
        ))}
    </React.Fragment>
}

export function renderDivElement(element: RichTextElement,
                                 renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    return <Box key={element.uuid}>
        {element.children?.map((child, index) => (
            <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-div-${index}`}>
                {renderChildrenRecursively(child, index)}
            </React.Fragment>
        ))}
    </Box>
}


export function renderTBodyElement(element: RichTextElement,
                                   renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    return <TableBody key={element.uuid}>
        {element.children?.map((child, index) => (
            <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-tbody-${index}`}>
                {renderChildrenRecursively(child, index)}
            </React.Fragment>
        ))}
    </TableBody>
}

export function renderTableElement(element: RichTextElement,
                                   renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    const classes = getAttribute(element.attributes!, "class");
    return <div className={classes && classes.includes('styled-table') ? 'tableContainer' : ''}>
        <Table key={element.uuid} className={classes}>
            {element.children?.map((child, index) => (
                <React.Fragment
                    key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-table-${index}`}>
                    {renderChildrenRecursively(child, index)}
                </React.Fragment>
            ))}
        </Table></div>
}

export function renderTrElement(element: RichTextElement,
                                renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    return <TableRow key={element.uuid}>
        {element.children?.map((child, index) => (
            <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-tr-${index}`}>
                {renderChildrenRecursively(child, index)}
            </React.Fragment>
        ))}
    </TableRow>
}

export function renderTdElement(element: RichTextElement,
                                renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    const classes = getAttribute(element.attributes!, "class");
    return <TableCell tabIndex={0} key={element.uuid} className={classes}>
        {element.children?.map((child, index) => (
            <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-td-${index}`}>
                {renderChildrenRecursively(child, index)}
            </React.Fragment>
        ))}
    </TableCell>
}

export function renderThElement(element: RichTextElement,
                                renderChildrenRecursively: (...args: any[]) => JSX.Element | null) {
    const classes = getAttribute(element.attributes!, "class");
    return <TableCell tabIndex={0} variant={'head'} key={element.uuid} className={classes}>
        {element.children?.map((child, index) => (
            <React.Fragment key={(child as any)?.uuid ?? `${element.uuid ?? 'rt'}-th-${index}`}>
                {renderChildrenRecursively(child, index)}
            </React.Fragment>
        ))}
    </TableCell>
}

function getCtaButtonJustification(className: string) {
    const alignClass = getAlignClass(className);
    const justificationMapping: { [key: string]: 'flex-start' | 'center' | 'flex-end' } = {
        'align--left': 'flex-start',
        'align--center': 'center',
        'align--right': 'flex-end',
        'align--justify': 'flex-start'
    };

    return alignClass ? justificationMapping[alignClass] : 'flex-start';
}

function renderCtaButtonElement(element: RichTextElement, className: string,
                                justification: 'flex-start' | 'center' | 'flex-end') {
    const linkRichTextElement = element.children?.find(
        (child: RichTextElement) => child.name === 'a')
    if (!linkRichTextElement) {
        return null // do not render cta if there is no target link
    }
    const internalUrl = linkRichTextElement?.attributes?.find(
        (attribute) => attribute.name === 'data-href')?.value
    const externalUrl = linkRichTextElement?.attributes?.find(
        (attribute) => attribute.name === 'href')?.value
    const url = internalUrl || externalUrl;
    const href = isDownloadTarget(url!) ? getDownloadTargetLink(url!) : url;
    const target = getTarget(linkRichTextElement);

    const ctaText = linkRichTextElement?.children?.find(
        (child: RichTextElement) => child._type === 'Characters')?.data

    const ctaButtonColorMapping: { [key: string]: string } = {
        'cta-default': 'bgGreen',
        'cta-blue': 'bgBlue',
        'cta-yellow': 'bgYellow',
    };

    const buttonCustomStyle = buttonClasses[ctaButtonColorMapping[className]] ||
        buttonClasses[BUTTON_DEFAULT_COLOR];
    return (
        <Box
            key={element.uuid}
            sx={{
                display: 'flex',
                width: '100%',
                justifyContent: justification,
                padding: 2
            }}>
            <Button component="a"
                    id={"cta-" + linkRichTextElement.uuid} key={element.uuid}
                    href={href} target={target}
                    onClick={createLinkClickHandler({
                        href: href!,
                        linkTarget: target as any,
                        isExternalOverride: isUrlAbsolute(href!),
                    })}
                    variant="contained" sx={buttonCustomStyle}>
                <Typography variant='ctaButton'>
                    {ctaText}
                </Typography>
            </Button>
        </Box>
    );
}
